import { useState, useEffect } from 'react';
import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';
import { logger } from '@/lib/logger';
import { persistentLogger } from '@/lib/persistent-logger';

export interface NetworkStatus {
  isOnline: boolean;
  connectionType: string;
  isSlowConnection: boolean;
}

export const useNetworkStatus = () => {
  const [status, setStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    connectionType: 'unknown',
    isSlowConnection: false
  });

  useEffect(() => {
    const updateNetworkStatus = async () => {
      if (Capacitor.isNativePlatform()) {
        // Native: usar Capacitor Network API
        const networkStatus = await Network.getStatus();
        const connType = String(networkStatus.connectionType);
        const newStatus = {
          isOnline: networkStatus.connected,
          connectionType: connType,
          isSlowConnection: connType === '2g' || connType === 'slow-2g'
        };
        
        // Log only when status changes
        if (status.isOnline !== newStatus.isOnline) {
          persistentLogger.addLog('NETWORK', newStatus.isOnline ? 'info' : 'warn', 
            `Network ${newStatus.isOnline ? 'connected' : 'disconnected'}`, {
            connectionType: connType,
            isSlowConnection: newStatus.isSlowConnection
          });
        }
        
        setStatus(newStatus);
      } else {
        // Web: usar navigator.connection
        const connection = (navigator as any).connection;
        const newStatus = {
          isOnline: navigator.onLine,
          connectionType: connection?.effectiveType || 'unknown',
          isSlowConnection: connection?.effectiveType === '2g' || 
                           connection?.effectiveType === 'slow-2g'
        };
        
        // Log only when status changes
        if (status.isOnline !== newStatus.isOnline) {
          persistentLogger.addLog('NETWORK', newStatus.isOnline ? 'info' : 'warn', 
            `Network ${newStatus.isOnline ? 'connected' : 'disconnected'}`, {
            connectionType: newStatus.connectionType
          });
        }
        
        setStatus(newStatus);
      }
    };

    updateNetworkStatus();

    if (Capacitor.isNativePlatform()) {
      Network.addListener('networkStatusChange', (status) => {
        logger.info('Network status changed (native)', status);
        updateNetworkStatus();
      });
    } else {
      window.addEventListener('online', updateNetworkStatus);
      window.addEventListener('offline', updateNetworkStatus);
    }

    return () => {
      if (!Capacitor.isNativePlatform()) {
        window.removeEventListener('online', updateNetworkStatus);
        window.removeEventListener('offline', updateNetworkStatus);
      }
    };
  }, []);

  return status;
};
